import { useEffect } from "react";
import { PromptInjectPayload } from "src/stores/tool-actions";
import { useStore } from "../stores";

/**
 * Tool Actions Hooks
 *
 * This module provides hooks for inter-component communication, including:
 *
 * 1. DATA KEY APPLY SYSTEM (existing):
 *    - Components can apply values to form steps by data key ID
 *    - Form steps listen for applies and update their values
 *    - Supports diff visualization for changes
 *
 * 2. PROMPT INJECTION SYSTEM (new):
 *    - Components can inject prompts into the chat input
 *    - Chat component listens for prompt events and updates input
 *    - Supports both replacing and appending to existing input
 *
 * Usage Examples:
 *
 * // To access tool actions directly from any component:
 * const { toolActionsStore } = useStore();
 * toolActionsStore.applyDataKey(dataKeyId, value);
 *
 * // To inject a prompt from any component:
 * const { injectPrompt } = usePromptInjection();
 * injectPrompt("Help me write better content", { replaceInput: false });
 *
 * // To listen for prompts in chat component:
 * usePromptInjectionListener((prompt, replaceInput) => {
 *   if (replaceInput) setInput(prompt);
 *   else setInput(prev => prev + "\n" + prompt);
 * });
 */

/**
 * Hook specifically for form components that need to listen for data key applies
 * Automatically handles the apply event when it matches the current step
 */
export const useDataKeyApplyListener = (
  dataKeyId: string,
  onApply: (value: string) => void,
  options: {
    autoMarkAsHandled?: boolean;
    clearOnUnmount?: boolean;
  } = {}
) => {
  const { toolActionsStore } = useStore();

  const { autoMarkAsHandled = true, clearOnUnmount = true } = options;

  // Check for pending applies and handle them
  useEffect(() => {
    if (toolActionsStore.hasPendingApply(dataKeyId)) {
      const applyEvent = toolActionsStore.getLatestDataKeyApply(dataKeyId);
      if (applyEvent) {
        onApply(applyEvent.payload.value);

        if (autoMarkAsHandled) {
          toolActionsStore.markApplyAsHandled(dataKeyId);
        }
      }
    }
  }, [
    dataKeyId,
    toolActionsStore.hasPendingApply(dataKeyId),
    onApply,
    autoMarkAsHandled,
    toolActionsStore,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (clearOnUnmount) {
        toolActionsStore.clearDataKeyEvents(dataKeyId);
      }
    };
  }, [dataKeyId, clearOnUnmount, toolActionsStore]);

  return {
    hasPendingApply: toolActionsStore.hasPendingApply(dataKeyId),
    latestApply: toolActionsStore.getLatestDataKeyApply(dataKeyId),
  };
};

/**
 * Hook specifically for handling diff visualization
 * Returns diff data and utility functions for rendering diffs
 */
// export const useDiffVisualization = (
//   dataKeyId: string,
//   toolCallId: string,
//   documents: Document[]
// ) => {
//   const { toolActionsStore } = useStore();

//   const diffResult = toolActionsStore.getDiffResult(dataKeyId, toolCallId);

//   return {
//     diffResult,
//     hasDiff: toolActionsStore.hasDiff(dataKeyId, toolCallId),
//     currentValue: getDocumentAnswerByDataKeyId(dataKeyId, documents),
//     isMarkdownTable: diffResult?.isMarkdownTable || false,
//     tableDiff: diffResult?.tableDiff,
//     diffHtml: diffResult?.diffHtml || [],
//   };
// };

/**
 * Hook for components that want to inject prompts into the chat
 * Provides a simple interface to send prompts
 */
export const usePromptInjection = () => {
  const { toolActionsStore } = useStore();

  return {
    // Inject a prompt into the chat input
    injectPrompt: (options: PromptInjectPayload) => {
      toolActionsStore.injectPrompt(options);
    },
  };
};

/**
 * Hook for the chat component to listen for prompt injection events
 * Automatically handles prompt events when they occur
 */
export const usePromptInjectionListener = (
  onPromptReceived: ({
    prompt,
    preview,
    replaceInput,
    submit,
  }: PromptInjectPayload & { eventId: string }) => void,
  options: {
    autoMarkAsHandled?: boolean;
    clearOnUnmount?: boolean;
  } = {}
) => {
  const { toolActionsStore } = useStore();
  const { autoMarkAsHandled = true, clearOnUnmount = true } = options;

  // Check for pending prompt injects and handle them
  useEffect(() => {
    if (toolActionsStore.hasPendingPromptInject()) {
      const promptEvent = toolActionsStore.getLatestPromptInject();
      if (promptEvent) {
        onPromptReceived({
          ...promptEvent.payload,
          eventId: promptEvent.id,
          // contextData,
        });

        if (autoMarkAsHandled) {
          toolActionsStore.markPromptInjectAsHandled(promptEvent.id);
        }
      }
    }
  }, [
    toolActionsStore.hasPendingPromptInject(),
    onPromptReceived,
    autoMarkAsHandled,
    toolActionsStore,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (clearOnUnmount) {
        toolActionsStore.clearPromptInjectEvents();
      }
    };
  }, [clearOnUnmount, toolActionsStore]);

  return {
    hasPendingPrompt: toolActionsStore.hasPendingPromptInject(),
    latestPrompt: toolActionsStore.getLatestPromptInject(),
  };
};
